/**
 * Hooks over the phone chart cache (locationChartCache): one location's
 * chart data for the Location tab, and every location's for a Charts tab
 * overlay. Both paint what the cache holds at once, load what is missing
 * (windowed by the selected range), refresh quietly when the page's
 * refresh signal changes, and refresh quietly on mount when what is held is
 * over a minute old. A failed refresh keeps the data on screen.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/AuthContext';
import type { ChartDataResponse, ChartSeriesKey, LocationStatus } from '../../../services/pours';
import { apiErrorText } from '../../../utils/apiError';
import { isHumidityLocation } from '../../../utils/locationChannels';
import { locationHasMaturity } from '../../../utils/locationMaturity';
import type { LocationChart } from '../overlayModel';
import { chartRangeMs, type ChartRangeKey } from './chartDelta';
import {
  chartCacheKey,
  chartEntryStatus,
  hasChartSeries,
  loadLocationChart,
  peekChartEntry,
  subscribeChartCache,
} from './locationChartCache';

/** Re-render when one of the cache entries (keys joined by '\n') changes;
 *  returns a counter that moves on each change. */
const useChartCacheVersion = (keysKey: string): number => {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const keys = new Set(keysKey.split('\n'));
    return subscribeChartCache((changed) => {
      if (changed === null || keys.has(changed)) setVersion((v) => v + 1);
    });
  }, [keysKey]);
  return version;
};

/** The last load a hook ran: what it was for, and how it went. */
interface LoadState {
  /** Which data the load was for (the hook's own key). */
  key: string;
  busy: boolean;
  quiet: boolean;
  error: string | null;
}

const IDLE: LoadState = { key: '', busy: false, quiet: false, error: null };

export interface LocationChartState {
  /** The location's chart data as held (null before the first reply). */
  data: ChartDataResponse | null;
  /** Loading with nothing to show yet: the chart shows its skeleton. */
  firstLoading: boolean;
  /** A load in flight while data is on screen. */
  refreshing: boolean;
  /** The load failed with nothing to show (a failed quiet refresh with data
   *  on screen is not an error). */
  error: string | null;
}

/**
 * One location's chart data for `need` (e.g. ['temp', 'tank', 'ea']) over
 * `range`. No location, no pour or an empty `need` loads nothing (e.g. a
 * weather station, or a location waiting for its first reading).
 */
export const useLocationChart = (
  pourId: string,
  location: LocationStatus | null | undefined,
  need: ReadonlyArray<ChartSeriesKey>,
  refreshSignal: number | undefined,
  pourStatus: string | null | undefined,
  range: ChartRangeKey,
): LocationChartState => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const userId = user?.UserID ?? null;
  const locationId = location?.id ?? '';
  const needKey = need.join(',');
  const rangeMs = chartRangeMs(range);
  const key = chartCacheKey(userId, pourId, locationId);
  useChartCacheVersion(key);

  // The latest summary row and status, read when a load runs: a poll's new
  // objects must not start loads on their own (the refresh signal does).
  const locationRef = useRef(location);
  locationRef.current = location;
  const statusRef = useRef(pourStatus);
  statusRef.current = pourStatus;

  const [load, setLoad] = useState<LoadState>(IDLE);
  const seq = useRef(0);

  const run = useCallback(async (quiet: boolean) => {
    const loc = locationRef.current;
    if (!pourId || !loc || !needKey) return;
    const runKey = chartCacheKey(userId, pourId, loc.id);
    const mine = ++seq.current;
    setLoad({ key: runKey, busy: true, quiet, error: null });
    try {
      await loadLocationChart({
        userId,
        pourId,
        location: loc,
        need: needKey.split(',') as ChartSeriesKey[],
        pourStatus: statusRef.current,
        rangeMs,
        quiet,
      });
      if (mine === seq.current) setLoad({ key: runKey, busy: false, quiet, error: null });
    } catch (err) {
      if (mine !== seq.current) return;
      console.error('Error fetching chart data:', err);
      setLoad({
        key: runKey,
        busy: false,
        quiet,
        error: apiErrorText(err, t('mobile.charts.loadFailed', { defaultValue: 'Could not load the chart data' })),
      });
    }
  }, [userId, pourId, needKey, rangeMs, t]);

  // A location, need or range not held loads (with a skeleton when nothing
  // is held); data over a minute old refreshes quietly.
  useEffect(() => {
    const loc = locationRef.current;
    if (!pourId || !loc || !needKey) return;
    const status = chartEntryStatus({
      userId,
      pourId,
      location: loc,
      need: needKey.split(',') as ChartSeriesKey[],
      pourStatus: statusRef.current,
      rangeMs,
    });
    if (status === 'missing') run(false);
    else if (status === 'stale') run(true);
  }, [run, locationId, userId, pourId, needKey, rangeMs]);

  // The page's refresh reloads quietly. Only a change of the signal
  // triggers it (not the first render).
  const lastRefreshSignal = useRef(refreshSignal);
  useEffect(() => {
    if (refreshSignal === lastRefreshSignal.current) return;
    lastRefreshSignal.current = refreshSignal;
    run(true);
  }, [refreshSignal, run]);

  const active = !!pourId && !!locationId && needKey !== '';
  const data = locationId ? peekChartEntry(key)?.data ?? null : null;
  const ready = active && hasChartSeries(key, need);
  const mine = load.key === key;
  // Nothing held yet and no failed load for this location: loading (also
  // on the render before the load starts).
  const firstLoading = active && !ready && !(mine && !load.busy && load.error);
  return {
    data,
    firstLoading,
    refreshing: active && ready && mine && load.busy,
    error: mine && !load.busy && load.error && (!ready || !load.quiet) ? load.error : null,
  };
};

export interface OverlayChartsState {
  /** Each location with data for `need`, in the order given. */
  charts: LocationChart[];
  /** Loading with nothing to show yet. */
  firstLoading: boolean;
  /** A load in flight while charts are on screen. */
  refreshing: boolean;
  /** Every location failed with nothing to show. */
  error: string | null;
}

/**
 * Every location's chart data for `need` over `range`, for an overlay chart
 * (LocationChart, as MultiLocationChart builds it). Each location loads and
 * fails on its own and keeps its last data; loads are keyed on the location
 * IDs, never on the array, so a poll's new array reloads nothing on its own.
 */
export const useOverlayCharts = (
  pourId: string,
  locations: ReadonlyArray<LocationStatus>,
  need: ReadonlyArray<ChartSeriesKey>,
  refreshSignal: number | undefined,
  pourStatus: string | null | undefined,
  range: ChartRangeKey,
): OverlayChartsState => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const userId = user?.UserID ?? null;
  const locationIds = locations.map((l) => l.id).join(',');
  const needKey = need.join(',');
  const rangeMs = chartRangeMs(range);
  const keysKey = locations.map((l) => chartCacheKey(userId, pourId, l.id)).join('\n');
  const version = useChartCacheVersion(keysKey);

  const locationsRef = useRef(locations);
  locationsRef.current = locations;
  const statusRef = useRef(pourStatus);
  statusRef.current = pourStatus;

  const loadKey = `${userId ?? ''}|${pourId}|${locationIds}|${needKey}|${range}`;
  const [load, setLoad] = useState<LoadState>(IDLE);
  const seq = useRef(0);

  const run = useCallback(async (quiet: boolean) => {
    const locs = locationsRef.current;
    if (!pourId || !needKey || locs.length === 0) return;
    const mine = ++seq.current;
    setLoad({ key: loadKey, busy: true, quiet, error: null });
    const results = await Promise.allSettled(
      locs.map((loc) =>
        loadLocationChart({
          userId,
          pourId,
          location: loc,
          need: needKey.split(',') as ChartSeriesKey[],
          pourStatus: statusRef.current,
          rangeMs,
          quiet,
        }),
      ),
    );
    if (mine !== seq.current) return;
    const failed = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
    if (failed.length > 0) console.error('Error fetching chart data:', failed[0].reason);
    setLoad({
      key: loadKey,
      busy: false,
      quiet,
      error: failed.length > 0 && failed.length === results.length
        ? apiErrorText(failed[0].reason, t('mobile.charts.loadFailed', { defaultValue: 'Could not load the chart data' }))
        : null,
    });
  }, [userId, pourId, needKey, rangeMs, loadKey, t]);

  // Locations, need or range not held load; data over a minute old
  // refreshes quietly.
  useEffect(() => {
    const locs = locationsRef.current;
    if (!pourId || !needKey || locs.length === 0) return;
    const statuses = locs.map((loc) =>
      chartEntryStatus({
        userId,
        pourId,
        location: loc,
        need: needKey.split(',') as ChartSeriesKey[],
        pourStatus: statusRef.current,
        rangeMs,
      }),
    );
    if (statuses.includes('missing')) run(false);
    else if (statuses.includes('stale')) run(true);
  }, [run, userId, pourId, needKey, rangeMs, locationIds]);

  const lastRefreshSignal = useRef(refreshSignal);
  useEffect(() => {
    if (refreshSignal === lastRefreshSignal.current) return;
    lastRefreshSignal.current = refreshSignal;
    run(true);
  }, [refreshSignal, run]);

  // Rebuilt when an entry changes (version) or the locations' names or
  // channel types change, so the overlay's memoised options stay put
  // otherwise.
  const locationsSignature = locations
    .map((l) => `${l.id}\u0001${l.name ?? ''}\u0001${l.logger_code ?? ''}\u0001${l.channel_type ?? ''}\u0001${l.monitor_type ?? ''}\u0001${l.logger_type ?? ''}`)
    .join('\u0002');
  const charts = useMemo(() => {
    const need0 = needKey ? (needKey.split(',') as ChartSeriesKey[]) : [];
    if (!pourId || need0.length === 0) return [];
    return locationsRef.current.flatMap((loc): LocationChart[] => {
      const key = chartCacheKey(userId, pourId, loc.id);
      const data = peekChartEntry(key)?.data;
      if (!data || !hasChartSeries(key, need0)) return [];
      return [{
        id: loc.id,
        // Location name first: "Castle Hill Humidity" reads far better in
        // the legend than "AU001930-4".
        name: loc.name || loc.logger_code || loc.id,
        isHumidity: isHumidityLocation(loc),
        hasMaturity: locationHasMaturity(loc),
        chart: data,
      }];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, locationsSignature, userId, pourId, needKey]);

  const active = !!pourId && needKey !== '' && locations.length > 0;
  const mine = load.key === loadKey;
  return {
    charts,
    firstLoading: active && charts.length === 0 && !(mine && !load.busy),
    refreshing: active && charts.length > 0 && mine && load.busy,
    error: active && charts.length === 0 && mine && !load.busy ? load.error : null,
  };
};
